import { clip, type Bones, type Clip } from '../clip.ts';
import { TAU, envelope, inCubic, inOut, inQuad, outBack, outCubic, outExpo, outQuad, phase, settle, span } from '../curves.ts';
import { apply, timeline, type KeyPose } from '../pose.ts';

/*
 * Tejedora (models/weaver.ts). Idle loops with the legs testing the ground one after another and the front pair
 * feeling the air; walk is the spider's alternating tetrapod (right 1 and 3 with left 2 and 4, then the other
 * four), each leg a beat behind the one in front so the steps ripple; attack comes in already reared, front legs
 * high and fangs open, and bites at ~0.13 s; hit, stun (loop), die (the abdomen swells and bursts, the legs curl in
 * and it rolls onto its back) and `weave` ('Encapullar'): it rears, swings its abdomen up over its back to aim the
 * spinnerets at the tower ahead, shoots the silk (the `silk` bone scales out) and hauls on it hand over hand.
 *
 * Conventions: +X on the body pitches the nose down (−X rears); +X on the abdomen lifts its tail. −X on `chelL`/
 * `chelR` swings the fangs up and open. Each leg's local +Z runs out along it: −X on `leg<i>` lifts it, a Y turn
 * swings it (see `leg`), +X on `knee<i>`/`foot<i>` folds them in. Legs 0–3 are the right side front to back, 4–7
 * the left.
 */

const LEGS = [0, 1, 2, 3, 4, 5, 6, 7] as const;
type L = (typeof LEGS)[number];
type LegB = `leg${L}` | `knee${L}` | `foot${L}`;
export type B = 'root' | 'body' | 'abdomen' | 'silk' | 'chelL' | 'chelR' | 'venom' | 'palpL' | 'palpR' | LegB;
type P = KeyPose<B>;

const legN = (i: number): B => `leg${i}` as B;
const kneeN = (i: number): B => `knee${i}` as B;
const footN = (i: number): B => `foot${i}` as B;
/** +1 right side, −1 left. */
const side = (i: number): number => (i < 4 ? 1 : -1);
/** 0 front … 3 rear. */
const row = (i: number): number => i % 4;
/** The alternating tetrapods: R1, R3, L2, L4 (0) against R2, R4, L1, L3 (1). */
const tetra = (i: number): number => (i === 0 || i === 2 || i === 5 || i === 7 ? 0 : 1);

/** One leg's channels: `lift` raises it, `stride` swings the foot forward (+) or back (−), `splay` lowers the hip and opens the leg out, `curl` folds it up as dead spiders do. */
const legCh = (i: number, lift: number, stride: number, splay = 0, curl = 0): [[number, number, number], [number, number, number], [number, number, number]] => [
  [-0.26 * lift + 0.2 * splay + 1.15 * curl, -side(i) * stride * (row(i) === 0 || row(i) === 3 ? 0.3 : 0.26), 0],
  [0.14 * lift - 0.28 * splay + 1.0 * curl, 0, 0],
  [0.16 * lift + 0.12 * splay + 0.9 * curl, 0, 0],
];

const leg = (b: Bones<B>, i: number, lift: number, stride: number, splay = 0, curl = 0): void => {
  const [h, k, f] = legCh(i, lift, stride, splay, curl);
  b[legN(i)].rot(...h);
  b[kneeN(i)].rot(...k);
  b[footN(i)].rot(...f);
};

/** A key pose of all eight legs from a function of the leg index, plus anything else. */
export const legsPose = (fn: (i: number) => readonly [number, number, number?, number?], rest: P = {}): P => {
  const p: Record<string, [number, number, number]> = {};
  for (const i of LEGS) {
    const [lift, stride, splay = 0, curl = 0] = fn(i);
    const [h, k, f] = legCh(i, lift, stride, splay, curl);
    p[legN(i)] = h;
    p[kneeN(i)] = k;
    p[footN(i)] = f;
  }
  return { ...(p as P), ...rest };
};

/** A damped impulse at `t0` (0 before it). */
const kick = (t: number, t0: number, freq = 2.5, damp = 7): number => (t < t0 ? 0 : settle(t - t0, freq, damp));

/** A deterministic shiver (two sines at integer multiples of `f`). */
const shiver = (t: number, f: number): number => 0.6 * Math.sin(TAU * f * t) + 0.4 * Math.sin(TAU * f * 2 * t + 1.3);

/** Fangs open by `o` (radians each; − closes them hard). */
const fangs = (b: Bones<B>, o: number, d = 0): void => {
  b.chelR.rot(-o, 0, -d);
  b.chelL.rot(-o, 0, d);
};

/** Palps: `l`/`r` raise each one (− lowers it). */
const palps = (b: Bones<B>, l: number, r: number): void => {
  b.palpL.rot(-l, 0.1 * l, 0);
  b.palpR.rot(-r, -0.1 * r, 0);
};

/** Poised: body a little low between the high knees, the abdomen riding level, palps forward. */
const ST: P = legsPose(() => [0, 0, 0.1], {
  root: [0, 0, 0, 0, -0.012, 0],
  body: [0.03, 0, 0],
  abdomen: [0.04, 0, 0],
});

/** A venom drop swelling at the fangs and falling, `k` through its cycle. */
const drip = (b: Bones<B>, k: number): void => {
  b.venom.pos(0, -0.07 * k * k, 0).scale(Math.max(0.001, k < 0.85 ? 0.75 + 0.45 * k : (1 - k) * 7.5));
};

/**
 * At rest it waits as spiders wait, still and then not: one leg at a time lifts and taps the ground twice, feeling
 * for footsteps, the front pair rise in turn to taste the air, the palps work, the abdomen swells and settles, and
 * now and then the whole body twitches round a few degrees and holds.
 */
const idle = (): Clip<B> =>
  clip<B>('idle', 4, true, (b, t) => {
    const ph = phase(t, 4);
    const p = ph * TAU;
    apply(b, ST);
    const br = Math.sin(p * 2);
    b.abdomen.scale(1 + 0.03 * br).rot(0.03 * br, 0.04 * Math.sin(p), 0);
    b.body.pos(0, 0.006 * br, 0);
    // The twitch: a quick turn toward something, held, and a slow turn back.
    const tw = span(ph, 0.42, 0.45, outExpo) - span(ph, 0.7, 0.9, inOut);
    b.root.rot(0, 0.09 * tw, 0);
    b.body.rot(-0.04 * tw, 0.04 * tw, 0);
    // Front legs in turn rise and feel the air, then set down again.
    for (const [i, a] of [[0, 0.05], [4, 0.55]] as const) {
      const k = envelope(ph, a, a + 0.07, a + 0.22, a + 0.3);
      const wave = Math.sin(TAU * 6 * t + i) * k;
      leg(b, i, 1.8 * k + 0.25 * wave, 0.4 * k + 0.2 * wave, 0);
    }
    // The others tap the ground in turn, twice each.
    for (const i of [1, 6, 3, 2, 5, 7] as const) {
      const a = [0.18, 0.32, 0.5, 0.68, 0.8, 0.92][[1, 6, 3, 2, 5, 7].indexOf(i)] as number;
      const d = ph - a;
      const k = d > 0 && d < 0.08 ? Math.abs(Math.sin((d / 0.08) * Math.PI * 2)) : 0;
      leg(b, i, 0.7 * k, 0.1 * k, 0);
    }
    palps(b, 0.12 + 0.15 * Math.max(0, Math.sin(p * 3)) * Math.sin(p * 9), 0.12 + 0.15 * Math.max(0, Math.sin(p * 3 + 2)) * Math.sin(p * 9 + 1));
    fangs(b, 0.04 + 0.04 * Math.max(0, Math.sin(p * 2 + 1)) ** 4);
    drip(b, phase(t, 2));
    b.silk.rot(0.1 * Math.sin(p), 0.15 * Math.sin(p * 2 + 1), 0);
  }, (t) => {
    const br = Math.sin(phase(t, 4) * TAU * 2);
    return [1 + 0.004 * br, 1 - 0.006 * br, 1 + 0.004 * br];
  });

/**
 * The tetrapod gait: four legs push while the other four swing through the air, then they trade. Each leg runs a
 * beat behind the one in front, so the steps ripple back along each side; the body glides level with a slight
 * sway, the heavy abdomen swinging a beat late, and the palps feel the way ahead.
 */
const walk = (dur = 0.9): Clip<B> =>
  clip<B>('walk', dur, true, (b, t) => {
    const ph = phase(t, dur);
    const th = ph * TAU;
    apply(b, ST, 0.8);
    for (const i of LEGS) {
      // Half the cycle on the ground sweeping back, half in the air swinging forward.
      const u = (ph + (tetra(i) ? 0.5 : 0) - row(i) * 0.05 + 1) % 1;
      const swing = u >= 0.5;
      const k = swing ? (u - 0.5) / 0.5 : u / 0.5;
      const stride = swing ? -Math.cos(Math.PI * k) : Math.cos(Math.PI * k);
      const lift = swing ? Math.sin(Math.PI * k) ** 0.7 : 0;
      leg(b, i, 1.1 * lift, 1.15 * stride, 0.08);
    }
    const s2 = Math.sin(2 * th);
    b.root.pos(0, -0.004 - 0.006 * Math.cos(2 * th), 0.01 * s2);
    b.body.rot(0.015 * Math.sin(2 * th - 0.8), 0.035 * Math.sin(th), 0.03 * Math.sin(th - 0.3));
    b.abdomen.rot(0.03 * Math.cos(2 * th - 1.4), -0.07 * Math.sin(th - 1.0), 0.03 * Math.sin(th - 1.2));
    palps(b, 0.15 + 0.25 * Math.max(0, Math.sin(th)), 0.15 + 0.25 * Math.max(0, -Math.sin(th)));
    fangs(b, 0.03 * Math.sin(2 * th));
    drip(b, phase(t, dur * 2));
    b.silk.rot(0.1 * Math.sin(th), 0.2 * Math.sin(th - 1.5), 0);
  });

/**
 * The bite: it comes in already reared, the front legs high and spread over its prey, the fangs swung open and
 * the venom swelling; at 0.13 s it drops on it, the front legs clamp down and in and the fangs stab; it holds a
 * moment, pumping, then lets go and settles.
 */
const COCK: P = legsPose((i) => (row(i) === 0 ? [3.0, 0.9, 0] : row(i) === 1 ? [1.4, 0.6, 0] : row(i) === 3 ? [0, -0.3, -0.2] : [0, -0.2, 0.1]), {
  root: [0, 0, 0, 0, 0.05, -0.06],
  body: [-0.42, 0, 0],
  abdomen: [0.32, 0, 0],
  chelR: [-0.7, 0, -0.15],
  chelL: [-0.7, 0, 0.15],
  palpR: [-0.6, -0.2, 0],
  palpL: [-0.6, 0.2, 0],
  venom: [0, 0, 0, 0, 0, 0.02, 1.35],
});
const BITE: P = legsPose((i) => (row(i) === 0 ? [0.3, 1.2, 0, 0.32] : row(i) === 1 ? [0.2, 0.7, 0.1, 0.12] : row(i) === 3 ? [0, -0.5, 0.2] : [0, -0.3, 0.2]), {
  root: [0, 0, 0, 0, -0.03, 0.1],
  body: [0.24, 0, 0],
  abdomen: [-0.12, 0, 0],
  chelR: [0.2, 0, 0.05],
  chelL: [0.2, 0, -0.05],
  palpR: [0.25, 0, 0],
  palpL: [0.25, 0, 0],
  venom: [0, 0, 0, 0, 0, 0.03, 1.5],
});

const attack = (): Clip<B> =>
  clip<B>('attack', 0.8, false, (b, t) => {
    timeline(b, t, [
      [0, { ...COCK, body: [-0.36, 0, 0] }],
      [0.06, COCK, outQuad],
      [0.13, BITE, inCubic],
      [0.32, { ...BITE, body: [0.18, 0, 0], root: [0, 0, 0, 0, -0.025, 0.08] }, outCubic],
      [0.8, ST, inOut],
    ], { abdomen: 0.05, palpL: 0.03, palpR: 0.03, venom: 0.02 });
    // The jolt of the bite through the body, and the abdomen pumping venom after it.
    const k = kick(t, 0.13, 5, 10);
    b.body.rot(0.04 * k);
    b.abdomen.rot(-0.06 * k).scale(1 + 0.04 * envelope(t, 0.15, 0.2, 0.3, 0.4) * Math.sin(TAU * 12 * t));
  }, (t) => {
    const k = Math.max(0, kick(t, 0.13, 3, 9));
    return [1 + 0.03 * k, 1 - 0.05 * k, 1 + 0.03 * k];
  });

/** The blow lands: it jerks back and sinks on its legs, the legs flinch out, the abdomen swings. */
const hit = (): Clip<B> =>
  clip<B>('hit', 0.4, false, (b, t) => {
    apply(b, ST);
    const k = settle(t, 2.6, 9) * span(t, 0, 0.03, outQuad);
    const a = Math.abs(k);
    b.root.pos(0, -0.02 * a, -0.05 * k);
    b.body.rot(-0.12 * k, 0.05 * k, 0.05 * k);
    b.abdomen.rot(0.15 * kick(t, 0.04, 3, 8), -0.08 * k, 0);
    fangs(b, 0.3 * a, 0.1 * a);
    palps(b, 0.4 * a, 0.4 * a);
    for (const i of LEGS) leg(b, i, row(i) === 0 ? 0.8 * a : 0, 0, 0.6 * a);
  }, (t) => {
    const k = Math.max(0, settle(t, 3, 9));
    return [1 + 0.03 * k, 1 - 0.05 * k, 1 + 0.03 * k];
  });

/** Dazed: low on splayed legs that twitch out of time, swaying, the palps limp, fangs hanging open. */
const stun = (): Clip<B> =>
  clip<B>('stun', 2, true, (b, t) => {
    const p = phase(t, 2) * TAU;
    apply(b, ST, 0.5);
    b.root.pos(0.02 * Math.cos(p), -0.03, 0).rot(0, 0.06 * Math.sin(p), 0.05 * Math.cos(p));
    b.body.rot(0.1 + 0.04 * Math.sin(p * 2), 0.06 * Math.sin(p), 0.04 * Math.cos(p));
    b.abdomen.rot(-0.08 + 0.05 * Math.sin(p + 1), 0.12 * Math.sin(p + 1.5), 0);
    fangs(b, 0.35 + 0.08 * Math.sin(p * 3), 0.1);
    palps(b, -0.3 + 0.1 * Math.sin(p * 2), -0.3 + 0.1 * Math.sin(p * 2 + 2));
    for (const i of LEGS) {
      const tw = Math.max(0, Math.sin(p * 3 + i * 1.7)) ** 4;
      leg(b, i, 0.6 * tw, 0.4 * Math.sin(p * 2 + i * 2.1) * tw, 0.55 + 0.1 * Math.sin(p + i));
    }
    b.silk.rot(-0.4, 0, 0);
  });

/**
 * Death: it rears with a shudder, legs clawing at the air, and the abdomen swells and bursts (the spiderlings
 * pour out of it then); the legs give and draw in, it tips onto its side and rolls onto its back with a thud;
 * there the legs curl up tight over the belly, twitching slower and slower.
 */
const die = (): Clip<B> =>
  clip<B>('die', 2.4, false, (b, t) => {
    const curl = (c: number): P => legsPose((i) => [0, 0.25 * (1.5 - row(i)), 0, c + 0.04 * row(i)]);
    timeline(b, t, [
      [0, ST],
      [0.2, { ...legsPose((i) => (row(i) < 2 ? [2.2, 0.6, 0] : [0, -0.2, 0.3])), root: [0, 0, 0, 0, 0.04, -0.04], body: [-0.35, 0, 0.04], abdomen: [0.2, 0, 0], chelR: [-0.6, 0, -0.2], chelL: [-0.6, 0, 0.2], palpR: [-0.7, 0, 0], palpL: [-0.7, 0, 0] }, outExpo],
      [0.55, { ...curl(0.45), root: [0, 0, 0.1, 0, -0.07, 0], body: [0.05, 0, 0.05], abdomen: [0.05, 0, 0], chelR: [-0.3, 0, 0], chelL: [-0.3, 0, 0] }, inOut],
      [0.9, { ...curl(0.6), root: [0, 0, 1.5, -0.25, 0.42, 0], body: [0, 0, 0], abdomen: [0.1, 0, 0] }, inQuad],
      [1.15, { ...curl(0.75), root: [0.12, 0, Math.PI, -0.04, 0.84, 0], chelR: [-0.4, 0, 0], chelL: [-0.4, 0, 0], palpR: [-0.5, 0, 0], palpL: [-0.5, 0, 0] }, inQuad],
      [1.3, { ...curl(0.8), root: [0.1, 0, Math.PI - 0.07, -0.04, 0.82, 0], chelR: [-0.4, 0, 0], chelL: [-0.4, 0, 0], palpR: [-0.5, 0, 0], palpL: [-0.5, 0, 0] }, outBack],
      [2.4, { ...curl(1.0), root: [0.1, 0, Math.PI - 0.04, -0.04, 0.8, 0], chelR: [-0.2, 0, 0], chelL: [-0.2, 0, 0], palpR: [-0.9, 0, 0], palpL: [-0.9, 0, 0] }, inOut],
    ], { abdomen: 0.05, palpL: 0.04, palpR: 0.04, chelL: 0.02, chelR: 0.02 });
    // The front legs claw the air as it rears; on its back they all twitch, slower and slower.
    const claw = envelope(t, 0.05, 0.12, 0.35, 0.5);
    for (const i of LEGS) {
      if (row(i) < 2) leg(b, i, 0.5 * Math.sin(TAU * 5 * t + i) * claw, 0.4 * Math.sin(TAU * 4 * t + i * 2) * claw);
    }
    const th = envelope(t, 1.2, 1.3, 1.6, 2.3, outQuad);
    for (const i of LEGS) leg(b, i, 0.4 * Math.sin(TAU * 4 * t + i * 1.9) * th, 0.3 * Math.sin(TAU * 3 * t + i * 2.7) * th, 0, 0.15 * Math.sin(TAU * 2.5 * t + i) * th);
    const sh = envelope(t, 0.1, 0.18, 0.45, 0.6) * shiver(t, 10);
    b.body.rot(0.03 * sh, 0, 0.03 * sh);
    // The abdomen swells until it bursts, and hangs slack after.
    const sw = span(t, 0.1, 0.42, inQuad) * (1 - span(t, 0.42, 0.5, outExpo));
    b.abdomen.scale(1 + 0.2 * sw - 0.16 * span(t, 0.44, 0.7, outQuad) + 0.03 * sh).rot(0.04 * sh);
    b.silk.scale(Math.max(0.001, 1 - span(t, 0.5, 1.0)));
  }, (t) => {
    const k = Math.max(0, kick(t, 1.15, 3, 9));
    return [1 + 0.05 * k, 1 - 0.08 * k, 1 + 0.05 * k];
  });

/**
 * Cocooning a tower: it rears and swings its abdomen up over its back until the spinnerets look at the tower
 * ahead, the mask on its back turned the same way; the silk shoots out in a long sheaf, and the front legs catch
 * it and haul hand over hand, the abdomen pumping, until it lets the silk go and settles.
 */
const weave = (): Clip<B> =>
  clip<B>('weave', 1.3, false, (b, t) => {
    const REAR: P = { ...legsPose((i) => (row(i) === 0 ? [3.0, 0.9, 0] : row(i) === 1 ? [0.9, 0.4, 0.1] : [0, -0.35, 0.3])), root: [0, 0, 0, 0, 0.04, -0.05], body: [-0.3, 0, 0], abdomen: [1.05, 0, 0], chelR: [-0.3, 0, 0], chelL: [-0.3, 0, 0], palpR: [-0.5, 0, 0], palpL: [-0.5, 0, 0] };
    timeline(b, t, [
      [0, ST],
      [0.28, REAR, outCubic],
      [1.02, { ...REAR, abdomen: [1.0, 0, 0], body: [-0.22, 0, 0] }],
      [1.3, ST, inOut],
    ], { abdomen: 0.06, palpL: 0.03, palpR: 0.03 });
    // The silk: aimed forward over its back and shot out, quivering while it hauls, then let go.
    const aim = envelope(t, 0.2, 0.36, 1.0, 1.25);
    const shot = span(t, 0.3, 0.46, outExpo) * (1 - span(t, 1.0, 1.24, inQuad));
    const haul = envelope(t, 0.42, 0.5, 0.95, 1.02);
    b.silk.rot(2.35 * aim + 0.05 * Math.sin(TAU * 6 * t) * haul, 0.06 * Math.sin(TAU * 3 * t) * haul, 0).scale(1 + 5.5 * shot + 0.35 * Math.sin(TAU * 4 * t) * haul);
    // Hand over hand: the front legs pull in turn, the second pair helping.
    for (const i of LEGS) {
      if (row(i) > 1) continue;
      const off = (side(i) > 0 ? 0 : 0.5) + row(i) * 0.25;
      const c = Math.sin(TAU * (4 * t + off));
      leg(b, i, (row(i) === 0 ? 0.9 : 0.4) * c * haul, (row(i) === 0 ? -0.7 : -0.3) * Math.cos(TAU * (4 * t + off)) * haul, 0, (row(i) === 0 ? 0.25 : 0.1) * Math.max(0, -c) * haul);
    }
    // The abdomen pumps the silk out.
    const pump = Math.sin(TAU * 8 * t) * haul;
    b.abdomen.scale(1 + 0.04 * pump).rot(0.04 * pump, 0, 0);
    b.body.rot(0.02 * pump, 0, 0.02 * Math.sin(TAU * 4 * t) * haul);
    fangs(b, 0.15 * aim);
  }, (t) => {
    const k = Math.max(0, kick(t, 0.32, 3, 8));
    return [1 - 0.02 * k, 1 + 0.03 * k, 1 - 0.02 * k];
  }, 'Encapullar');

/** Its clips (see models/weaver.ts for the bones). */
export const weaverClips = (): Clip[] => [idle(), walk(), attack(), hit(), stun(), die(), weave()] as Clip[];

/** Clips by library kind (see ../rigs.ts). */
export const CLIPS: Record<string, () => Clip[]> = { weaver: weaverClips };
